import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useLanguage } from '../i18n/LanguageContext'
import MetaTags from '../components/MetaTags'
import ScrollReveal from '../components/ScrollReveal'

export default function ServicesPage() {
  const { t, getPageUrl, getServiceUrl } = useLanguage()
  const [services, setServices] = useState([])

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/services')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) setServices(data)
      })
      .catch(() => {})
  }, [])

  const defaultServicesList = [
    {
      service_slug: 'specialtransport',
      icon_name: 'fa-truck-ramp-box',
      image: ['https://assets.auxodev.dk/assets/ed865070-334e-4849-904d-48dc93d1adc7/470065228-1004853678354078-8640809208142626719-n.jpg']
    },
    {
      service_slug: 'containerkoersel',
      icon_name: 'fa-boxes-stacked',
      image: ['https://assets.auxodev.dk/assets/ed865070-334e-4849-904d-48dc93d1adc7/470065228-1004853678354078-8640809208142626719-n.jpg']
    },
    {
      service_slug: 'stykgods',
      icon_name: 'fa-boxes-stacked',
      image: ['https://assets.auxodev.dk/assets/ed865070-334e-4849-904d-48dc93d1adc7/476816859-656735350025511-354367063843939379-n.jpg']
    },
    {
      service_slug: 'traepiller',
      icon_name: 'fa-wheat-awn',
      image: ['https://assets.auxodev.dk/assets/ed865070-334e-4849-904d-48dc93d1adc7/470183366-1004865451686234-2145888141808669137-n.jpg']
    },
    {
      service_slug: 'national-transport',
      icon_name: 'fa-truck-fast',
      image: ['https://assets.auxodev.dk/assets/ed865070-334e-4849-904d-48dc93d1adc7/476448836-655303320168714-1260139362731958487-n.jpg']
    },
    {
      service_slug: 'international-transport',
      icon_name: 'fa-globe',
      image: ['https://assets.auxodev.dk/assets/ed865070-334e-4849-904d-48dc93d1adc7/470160342-1004853748354071-1772744319645786737-n.jpg']
    },
  ]

  return (
    <div className="bg-slate-50 min-h-screen pb-20">
      <MetaTags
        title={t.servicesPage.metaTitle}
        description={t.servicesPage.metaDesc}
      />

      {/* Header Banner */}
      <section className="bg-gradient-to-br from-slate-100 via-sky-50/60 to-white text-slate-900 py-12 lg:py-16 border-b border-slate-200 relative overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-4 text-slate-900">
              {t.servicesPage.headerTitle}
            </h1>
            <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
              {t.servicesPage.headerSubtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {defaultServicesList.map((s, idx) => {
            const imgUrl = Array.isArray(s.image) && s.image.length > 0 ? s.image[0] : null
            const item = t.servicesList[s.service_slug] || {}
            const servicePath = getServiceUrl(s.service_slug)

            return (
              <ScrollReveal key={s.service_slug} variant="up" delay={idx * 100}>
                <div 
                  className="bg-white rounded-none border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between h-full"
                >
                  <div>
                    {imgUrl && (
                      <div className="h-52 overflow-hidden bg-slate-100">
                        <img 
                          src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/${imgUrl.replace('https://assets.auxodev.dk/', '')}`} 
                          alt={item.title || s.service_slug} 
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}

                    <div className="p-6">
                      <div className="flex items-center gap-3 text-sky-600 text-2xl mb-3">
                        <i className={`fa-solid ${s.icon_name || 'fa-truck'}`}></i>
                        <h2 className="text-xl font-bold text-slate-900">
                          {item.title}
                        </h2>
                      </div>

                      <p className="text-slate-600 text-sm leading-relaxed mb-6">
                        {item.shortDesc}
                      </p>
                    </div>
                  </div>

                  <div className="p-6 pt-0">
                    <Link
                      to={servicePath}
                      className="inline-flex items-center gap-2 text-sky-600 hover:text-sky-700 font-bold text-sm group"
                    >
                      <span>{t.servicesPage.readMoreBtn}</span>
                      <i className="fa-solid fa-arrow-right text-xs group-hover:translate-x-1 transition-transform"></i>
                    </Link>
                  </div>
                </div>
              </ScrollReveal>
            )
          })}
        </div>

        {/* CTA Banner */}
        <div className="mt-16 bg-slate-900 text-white p-8 sm:p-12 rounded-none shadow-md flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="max-w-2xl">
            <h3 className="text-2xl font-bold mb-2">{t.servicesPage.needAdviceTitle}</h3>
            <p className="text-slate-300 text-sm sm:text-base">
              {t.servicesPage.needAdviceDesc}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto shrink-0">
            <Link
              to={getPageUrl('contact')}
              className="bg-sky-500 hover:bg-sky-600 text-white font-bold py-3.5 px-6 rounded-none text-center text-sm shadow transition-all whitespace-nowrap"
            >
              {t.servicesPage.getInTouch}
            </Link>
            <a
              href="tel:+4525303016"
              className="bg-white/10 hover:bg-white/20 text-white border border-white/20 font-bold py-3.5 px-6 rounded-none text-center text-sm transition-all whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="fa-solid fa-phone text-xs"></i>
              <span>+45 25 30 30 16</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}
